import { useDebounceFn } from '@vueuse/core'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { useSubscriptionsStore } from './store'
import { useSubscriptionOptions } from './subscription-options'

const INPUT_DEBOUNCE_MS = 400

interface FiltersSubscriptionsReturn {
  refSearch: WritableComputedRef<string>
  refDateRange: WritableComputedRef<Date[] | null>
  refStatuses: WritableComputedRef<string[]>
  statusOptions: ComputedRef<AppSelectOption[]>
  isLoading: ComputedRef<boolean>
  clearFilters: () => void
  submitSearch: () => Promise<void>
}

export const useFiltersSubscriptions = (): FiltersSubscriptionsReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useSubscriptionsStore()
  const dayjs = useDayjs()
  const { statusOptions } = useSubscriptionOptions()

  /**
   * The one place a filter change reaches the URL: write the store, push the query (the page refetches on query change).
   * `page` is dropped so a new filter never lands on a page that no longer exists.
   */
  const syncQuery = async (): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        q: store.getFilterSearch || undefined,
        status: store.getFilterStatuses.length ? store.getFilterStatuses : undefined,
        from: store.getFilterFrom || undefined,
        to: store.getFilterTo || undefined,
        page: undefined,
      },
    })
  }

  const applyTyped = useDebounceFn(() => syncQuery(), INPUT_DEBOUNCE_MS)

  const refSearch = computed<string>({
    get: () => store.getFilterSearch ?? '',
    set: (value) => {
      store.setFilterSearch(value.trim() || null)
      void applyTyped()
    },
  })

  const refDateRange = computed<Date[] | null>({
    get: () => {
      const from = store.getFilterFrom
      const to = store.getFilterTo
      if (!from || !to) return null

      const fromDate = dayjs(from)
      const toDate = dayjs(to)

      return fromDate.isValid() && toDate.isValid() ? [fromDate.toDate(), toDate.toDate()] : null
    },
    set: (value) => {
      if (Array.isArray(value) && value.length === 2) {
        store.setFilterFrom(dayjs(value[0]).format(DateFormat.Default))
        store.setFilterTo(dayjs(value[1]).format(DateFormat.Default))
      } else {
        store.setFilterFrom(null)
        store.setFilterTo(null)
      }

      void syncQuery()
    },
  })

  const refStatuses = computed<string[]>({
    get: () => store.getFilterStatuses,
    set: (value) => {
      store.setFilterStatuses(value)
      void syncQuery()
    },
  })

  const isLoading = computed<boolean>(() => store.isLoading)

  const clearFilters = (): void => {
    store.resetFilters()
    void syncQuery()
  }

  /** Applies the filters at once instead of waiting out the typing debounce. */
  const submitSearch = (): Promise<void> => syncQuery()

  return {
    refSearch,
    refDateRange,
    refStatuses,
    statusOptions,
    isLoading,
    clearFilters,
    submitSearch,
  }
}
